import React from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { FileDownloadOutlined, MarkEmailReadOutlined } from '@mui/icons-material';
import type { StudentReportData } from '../../../services/analyticsService';
import { severityChipSx, paymentStatusSx } from '../../../utils/severityTone';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import { useTimezone } from '../../../hooks/useTimezone';
import { useLetterPdf } from '../../letters/useLetterPdf';
import { SectionCard } from '../../common/SectionCard';
import EmptyState from '../../common/EmptyState';
import BusyIcon from '../../common/BusyIcon';

type Letter = StudentReportData['administrativeData']['lettersIssued'][number];

/** Where a letter's fine stands, in words. */
const paymentLabel = (l: Letter, money: (n: number) => string): string =>
  l.paymentStatus === 'paid' ? `Paid ${money(l.amount ?? 0)}`
    : l.paymentStatus === 'partial' ? `Paid ${money(l.paidAmount ?? 0)} of ${money(l.amount ?? 0)}`
      : l.paymentStatus === 'waived' ? 'Waived'
        : `Unpaid ${money(l.amount ?? 0)}`;

/** The letters and fines issued to the student: how many of each kind, what's owed, and each letter to download. */
const LettersCard: React.FC<{ report: StudentReportData }> = ({ report }) => {
  const theme = useTheme();
  const money = useCurrencyFormat();
  const { formatDate } = useTimezone();
  const { download, busyId } = useLetterPdf();
  const { lettersIssued: letters, lettersSummary: sum } = report.administrativeData;

  if (letters.length === 0) {
    return (
      <SectionCard title="Letters & fines">
        <EmptyState dense icon={<MarkEmailReadOutlined />} title="No letters issued" description="Commendations, notices and fines for this student appear here." />
      </SectionCard>
    );
  }

  const owed = sum.outstandingAmount + sum.collectedAmount + sum.waivedAmount > 0;
  return (
    <SectionCard title="Letters & fines" action={<Typography variant="caption" color="text.secondary">{letters.length} issued</Typography>}>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: owed ? 1.5 : 1 }}>
        {Object.entries(sum.byCategory).map(([id, c]) => (
          <Chip key={id} size="small" label={`${c.label} · ${c.count}`} sx={{ height: 22, ...severityChipSx(theme, c.severity) }} />
        ))}
      </Stack>

      {owed && (
        <Stack direction="row" sx={{ mb: 1.5, borderRadius: 2, border: 1, borderColor: 'divider', '& > div + div': { borderLeft: 1, borderColor: 'divider' } }}>
          {[
            { label: 'Outstanding', value: sum.outstandingAmount, color: 'error.main' },
            { label: 'Collected', value: sum.collectedAmount, color: 'success.main' },
            { label: 'Waived', value: sum.waivedAmount, color: 'text.secondary' },
          ].map(t => (
            <Box key={t.label} sx={{ flex: 1, px: 1.25, py: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">{t.label}</Typography>
              <Typography variant="body2" fontWeight={700} sx={{ color: t.color }} noWrap>{money(t.value)}</Typography>
            </Box>
          ))}
        </Stack>
      )}

      <Box sx={{ '& > div + div': { borderTop: 1, borderColor: 'divider' } }}>
        {letters.map(l => (
          <Stack key={l._id} direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Chip label={l.category?.label || 'Letter'} size="small" sx={{ height: 20, fontSize: '0.68rem', ...severityChipSx(theme, l.category?.severity) }} />
                <Typography variant="caption" color="text.secondary" noWrap>{formatDate(l.issuedAt)}</Typography>
              </Stack>
              {(l.title || l.reason) && <Typography variant="body2" sx={{ mt: 0.5 }} noWrap title={l.title || l.reason}>{l.title || l.reason}</Typography>}
              {l.amount != null && l.amount > 0 && (
                <Chip label={paymentLabel(l, money)} size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.68rem', fontWeight: 600, ...paymentStatusSx(theme, l.paymentStatus) }} />
              )}
            </Box>
            <Tooltip title="Download letter">
              <span>
                <IconButton size="small" onClick={() => download(l._id)} disabled={!!busyId} aria-label="Download letter">
                  <BusyIcon busy={busyId === l._id}><FileDownloadOutlined fontSize="small" /></BusyIcon>
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ))}
      </Box>
    </SectionCard>
  );
};

export default LettersCard;
